import axios, { AxiosError } from 'axios';
import axiosRetry from 'axios-retry';
import { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setVendorData } from 'store/vendorSlice';
import { parseOrderItemsForGTM } from 'utils/gtmUtils';
import {
  OrderStatusType,
  STATUS_CANCELED,
  STATUS_EXPIRED,
  STATUS_PENDING_PAYMENT,
} from 'utils/orderStatuses';
import { ORDER_CONFIRMATION_PAGE_TYPE_URL } from 'utils/static/constants';
import { EventData, OrderData, initialEventData } from './types';

const useOrderConfirmationPageData = orderId => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const initialOrderData = {
    uuid: orderId || '',
    status: STATUS_PENDING_PAYMENT as OrderStatusType,
    pendingReason: '',
    placedAt: '',
    createdAt: '',
    numberOfTickets: 0,
    hasAddonsOrTopUps: false,
    hasNFT: false,
    dueDays: 15,
    tickets: [],
    invoice: '',
    order_total: {
      currency: '',
      net_price: 0,
      tax_amount: 0,
    },
    customer_info: {
      email: '',
      name: '',
    },
  };

  const [orderData, setOrderData] = useState<OrderData>(initialOrderData);
  const [eventData, setEventData] = useState<EventData>(initialEventData);
  const [isLoading, setIsLoading] = useState(true);

  const getInitialData = async () => {
    try {
      axiosRetry(axios, {
        retries: 7,
        shouldResetTimeout: true,
        retryCondition: error => error.code === 'ECONNABORTED',
        retryDelay: axiosRetry.exponentialDelay,
      });
      const { data: orderAndEventData } = await axios.post(
        `${process.env.REACT_APP_API_URL}/order?include=mustCompleteRegistrationForms,numberOfTickets,createdAt,placedAt,status,pendingReason,event,event.location,event.timeInterval,event.dateTimeFormat,event.checkoutConfiguration.displayTickets,event.checkoutConfiguration.orderConfirmationPageType,event.checkoutConfiguration.orderConfirmationPageText,event.checkoutConfiguration.orderConfirmationPageUrl,event.registrationform,dueDays,tickets,tickets.dateTime,tickets.addons,tickets.wallet_credit,tickets.registration_form,invoices,order_total,order_items.details,event.seller_public_information,event.sellerFeatures,customer_info,hasNFT`,
        {
          order_id: orderId,
        },
        {
          timeout: 10000,
        },
      );

      const { orderConfirmationPageType, orderConfirmationPageUrl } =
        orderAndEventData.event.checkoutConfiguration;

      if (orderConfirmationPageType === ORDER_CONFIRMATION_PAGE_TYPE_URL) {
        window.location.replace(decodeURIComponent(orderConfirmationPageUrl));
      } else {
        if (orderAndEventData.mustCompleteRegistrationForms) {
          navigate(`/order/assign-tickets?uuid=${orderId}`);
        }

        const {
          uuid,
          status,
          pendingReason,
          placedAt,
          createdAt,
          numberOfTickets,
          hasAddonsOrTopUps,
          hasNFT,
          dueDays,
          tickets,
          event,
          invoices,
          order_total,
          order_items,
          customer_info,
        } = orderAndEventData;
        const order = {
          uuid,
          status,
          pendingReason,
          placedAt,
          createdAt,
          numberOfTickets,
          hasAddonsOrTopUps,
          hasNFT,
          dueDays,
          tickets,
          invoice: invoices[0] || '',
          order_total,
          customer_info,
        };
        setOrderData(order);
        setEventData(event);
        setIsLoading(false);
        dispatch(
          setVendorData({
            id: event.id_seller,
            features: event.sellerFeatures,
            publicInformation: event.seller_public_information,
          }),
        );
        if (event.registrationform.length === 0) {
          // GTM
          const tagManagerArgs = {
            gtmId: event.gtm_code,
            dataLayer: {
              event: 'purchase',
              ecommerce: {
                value: order_total.net_price,
                tax: order_total.tax_amount,
                transaction_id: uuid,
                currency: order_total.currency,
                items: parseOrderItemsForGTM(order_items),
              },
            },
          };

          TagManager.initialize(tagManagerArgs);
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.data.error[0].error_code === 422) {
          navigate('/404');
        }
      }
    }
  };

  useEffect(() => {
    if (orderId) {
      getInitialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // useEffect(() => {
  //   if (isLoading === false && orderData.status !== STATUS_COMPLETE) {
  //     const updateData = setInterval(async () => {
  //       const { data } = await axios.post(
  //         `${process.env.REACT_APP_API_URL}/order?include=status,pendingReason,tickets,tickets.dateTime,invoices,order_total`,
  //         {
  //           order_id: orderId,
  //         },
  //       );

  //       const {
  //         uuid,
  //         status,
  //         pendingReason,
  //         placedAt,
  //         createdAt,
  //         numberOfTickets,
  //         hasAddonsOrTopUps,
  //         tickets,
  //         invoices,
  //         order_total,
  //         customer_info,
  //       } = data;

  //       if (
  //         status !== orderData.status ||
  //         pendingReason !== orderData.pendingReason
  //       ) {
  //         setOrderData(data => ({
  //           uuid,
  //           status,
  //           pendingReason,
  //           placedAt,
  //           createdAt,
  //           numberOfTickets,
  //           hasAddonsOrTopUps,
  //           dueDays: data.dueDays,
  //           tickets,
  //           invoice: invoices[0] || '',
  //           order_total,
  //           customer_info,
  //         }));
  //       }

  //       if (status === STATUS_COMPLETE) {
  //         clearInterval(updateData);
  //       }
  //     }, 3000);

  //     return () => {
  //       clearInterval(updateData);
  //     };
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isLoading]);

  useEffect(() => {
    if (
      orderData.status === STATUS_EXPIRED ||
      orderData.status === STATUS_CANCELED
    ) {
      navigate('/404');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData.status]);

  return {
    isLoading,
    eventData,
    orderData,
  };
};

export default useOrderConfirmationPageData;
